import {
  Grid,
  GridItem,
  Text,
  SimpleGrid,
  Flex,
  Box,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';
import { Check, Info, X } from '@phosphor-icons/react';
import { RefObject } from 'react';
import { ListingProperty, ListingProps } from '@/types/listing';
import { usePropertyTabs } from '../PropertyTabs';
import { formatMaybeNumberString } from '@/lib/formatNumber';

interface DetailItemProps {
  value: string | number | null | undefined | boolean;
  label: string;
  description?: string | null;
  isTextValue?: boolean;
}

const DetailItem = ({
  value,
  label,
  description,
  isTextValue,
}: DetailItemProps) => {
  const { t } = useTranslation('generated_pdfs');
  // eslint-disable-next-line no-nested-ternary
  const renderValue = isTextValue
    ? value || '-'
    : value
    ? t('yes', 'Kyllä')
    : t('no', 'Ei'); // much simpler liker this
  const desc = description?.trim();

  return (
    <GridItem>
      <Flex width="100%">
        <Box minWidth="50px" maxWidth="50px" layerStyle="noPrint">
          {!isTextValue ? (
            <Icon
              as={value ? Check : X}
              color={value ? 'primary.500' : 'gray.400'}
              width={30}
              height={30}
            />
          ) : (
            <Icon as={Info} color="gray.400" width={30} height={30} />
          )}
        </Box>
        <Flex flexDir="column">
          <Text fontWeight="600" fontSize="md">
            {label}
          </Text>
          <Text
            fontWeight="400"
            fontSize="sm"
            mt={1}
            textTransform={!isTextValue ? 'uppercase' : 'none'}
            mb={1}
            whiteSpace="pre-line"
          >
            {renderValue}
          </Text>
          {desc && (
            <Text color="gray.600" fontSize="sm" mb={1} whiteSpace="pre-line">
              {desc}
            </Text>
          )}
        </Flex>
      </Flex>
    </GridItem>
  );
};

// const dataKeys = [
//   'property_id',
//   'property_name',
//   'property_legal_confirmation_of_possession_or_rental_right_regis',
// ];

const RenderData = ({ property }: { property?: ListingProperty }) => {
  const { t } = useTranslation(['generated_pdfs', 'backoffice']);
  if (!property) {
    return null;
  }
  const propertyData = (
    <>
      <Grid templateColumns={['1fr', null, null, '1fr 1fr']} gap={3} rowGap={8}>
        {/* <DetailItem
          isTextValue
          value={
            t(
              `backoffice:property_type_values.${property.property_type}`,
            ) as string
          }
          label={t('property_type', 'Kiinteistötyyppi')}
        /> */}
        <DetailItem
          value={property.has_buildings || property.other_buildings.length > 0}
          label={t('backoffice:has_buildings')}
        />
        {[
          property.property_plan_none,
          property?.property_plan_local,
          property?.property_plan_shoreline,
          property?.property_plan_master,
          property?.property_plan_regional,
        ].find((v) => v) ? (
          <DetailItem
            value={
              property?.property_plan_local ||
              property?.property_plan_shoreline ||
              property?.property_plan_master ||
              property?.property_plan_regional
            }
            label={t('property_land_use_planning', 'Kaavoitus')}
          />
        ) : null}
        <DetailItem
          value={property.property_has_a_road_to_destination}
          label={t(
            'backoffice:property_has_a_road_to_destination',
            'Tie Perille',
          )}
        />
        <DetailItem
          value={property.property_has_shoreline}
          label={t('backoffice:property_has_shoreline')}
          description={[
            formatMaybeNumberString(property?.property_shoreline_in_meters, {
              unit: 'm',
            }),
            property?.property_shoreline_type &&
              t(
                `property_shoreline_type_${property?.property_shoreline_type}`,
                property?.property_shoreline_type,
              ).toLowerCase(),
          ]
            .filter(Boolean)
            .join(', ')}
        />
      </Grid>
    </>
  );
  return propertyData;
};

const ListingDetailsBlock = ({
  listing,
  sectionRef,
}: Pick<ListingProps, 'listing'> & {
  sectionRef: RefObject<HTMLDivElement>;
}) => {
  const { tabsProps, tabListProps, tabPanelsProps, tabPanelProps } =
    usePropertyTabs({
      sectionRef,
    });

  if (!listing.properties?.length) {
    return null;
  }

  if (listing.properties.length === 1) {
    return (
      <SimpleGrid columns={1} spacingX={5} spacingY={7}>
        <RenderData property={listing.properties[0]} />
      </SimpleGrid>
    );
  }

  return (
    <>
      <Tabs {...tabsProps} gridColumn="aside !important">
        <TabList {...tabListProps} />
      </Tabs>
      <Tabs {...tabsProps}>
        <TabPanels {...tabPanelsProps}>
          {listing.properties.map((property) => (
            <TabPanel {...tabPanelProps} key={property.id}>
              <SimpleGrid columns={1} spacingX={5} spacingY={7}>
                <RenderData property={property} />
                {/* <DetailItem value={property.driving_instructions} label="Ajo-ohjeet" description={property.driving_instructions} /> */}
              </SimpleGrid>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </>
  );
};

export default ListingDetailsBlock;
